/**
 * Cross-device draft sync — payload type + wire codec.
 *
 * A draft is unsent, in-progress text the user has typed into a channel
 * composer. Kaether mirrors drafts between a user's own devices via
 * encrypted sync messages on the same Double Ratchet session as user
 * content; the server only sees an opaque `kaether:e2ee:v1:` envelope
 * and cannot read the draft, cannot rebind it to a different channel,
 * and cannot freeze your draft to an old value because every fresh
 * edit from any of your devices carries a newer ts.
 *
 * Wire format of the *plaintext that gets encrypted* (NOT the on-the-wire
 * envelope, which is the standard `kaether:e2ee:v1:` form):
 *
 *   kaether:draft:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   { "schema": 1, "channelId": "abc", "text": "hello", "ts": 1234567890 }
 *
 * Empty `text` is a valid "I cleared my draft" signal — it deletes the
 * peer's stored draft for that channel. The signed plaintext binds
 * `(channelId, text, ts)` so a hostile server cannot redirect a draft
 * onto a different channel.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/DraftMessage.swift`.
 */

// ---------------------------------------------------------------------------
// Payload
// ---------------------------------------------------------------------------

export interface DraftPayload {
  readonly schema: number;
  readonly channelId: string;
  readonly text: string;
  readonly ts: number; // Unix seconds; Int64 on iOS — number is safe up to 2^53
}

export const DRAFT_SCHEMA_VERSION = 1;

/**
 * Construct a `DraftPayload` with the current schema version.
 */
export function makeDraftPayload(
  channelId: string,
  text: string,
  ts: number = Math.floor(Date.now() / 1000),
): DraftPayload {
  return { schema: DRAFT_SCHEMA_VERSION, channelId, text, ts };
}

// ---------------------------------------------------------------------------
// Codec errors
// ---------------------------------------------------------------------------

export type DraftCodecErrorKind =
  | { tag: "notADraft" }
  | { tag: "malformed"; reason: string }
  | { tag: "unsupportedSchema"; version: number }
  | { tag: "tooLarge"; count: number };

export class DraftCodecError extends Error {
  readonly kind: DraftCodecErrorKind;

  constructor(kind: DraftCodecErrorKind) {
    super(DraftCodecError.message(kind));
    this.name = "DraftCodecError";
    this.kind = kind;
  }

  private static message(k: DraftCodecErrorKind): string {
    switch (k.tag) {
      case "notADraft":
        return "not a draft plaintext";
      case "malformed":
        return `malformed draft: ${k.reason}`;
      case "unsupportedSchema":
        return `unsupported draft schema: ${k.version}`;
      case "tooLarge":
        return `draft text too large: ${k.count} code points (max ${DraftCodec.MAX_DRAFT_LENGTH})`;
    }
  }
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/**
 * Count Unicode scalar values (code points), not UTF-16 code units.
 *
 * JavaScript's `String.prototype.length` counts UTF-16 code units, which
 * means a single emoji like 🤦‍♂️ (U+1F926 U+200D U+2642 U+FE0F) counts as
 * more than one under `.length`. Spreading into an array via `[...str]`
 * iterates by code point so multi-scalar sequences are bounded correctly —
 * matching iOS `String.count` semantics.
 */
export function countCodePoints(str: string): number {
  return [...str].length;
}

export const DraftCodec = {
  PREFIX: "kaether:draft:v1:" as const,
  MAX_DRAFT_LENGTH: 10_000,

  /**
   * Returns true if `plaintext` begins with the draft sentinel prefix.
   * Used to cheaply route incoming decrypted payloads before calling `decode`.
   */
  isDraft(plaintext: string): boolean {
    return plaintext.startsWith(DraftCodec.PREFIX);
  },

  /**
   * Encode a `DraftPayload` to the `kaether:draft:v1:<base64url(JSON)>`
   * wire format.
   *
   * Throws `DraftCodecError` with kind `tooLarge` if `payload.text` exceeds
   * `MAX_DRAFT_LENGTH` Unicode code points.
   */
  encode(payload: DraftPayload): string {
    const len = countCodePoints(payload.text);
    if (len > DraftCodec.MAX_DRAFT_LENGTH) {
      throw new DraftCodecError({ tag: "tooLarge", count: len });
    }
    const json = JSON.stringify(payload);
    const b64 = base64UrlEncode(new TextEncoder().encode(json));
    return DraftCodec.PREFIX + b64;
  },

  /**
   * Decode a `kaether:draft:v1:` plaintext back to a `DraftPayload`.
   *
   * Throws:
   * - `DraftCodecError { tag: "notADraft" }` — prefix is absent.
   * - `DraftCodecError { tag: "malformed" }` — base64url or JSON is broken, or
   *   `channelId` is empty.
   * - `DraftCodecError { tag: "unsupportedSchema" }` — `schema` field is not 1.
   * - `DraftCodecError { tag: "tooLarge" }` — decoded `text` exceeds
   *   `MAX_DRAFT_LENGTH` code points (guards against maliciously-crafted
   *   ciphertexts that expand on decode).
   */
  decode(plaintext: string): DraftPayload {
    if (!plaintext.startsWith(DraftCodec.PREFIX)) {
      throw new DraftCodecError({ tag: "notADraft" });
    }
    const b64 = plaintext.slice(DraftCodec.PREFIX.length);
    let bytes: Uint8Array;
    try {
      bytes = base64UrlDecode(b64);
    } catch {
      throw new DraftCodecError({
        tag: "malformed",
        reason: "invalid base64url",
      });
    }

    let raw: unknown;
    try {
      raw = JSON.parse(new TextDecoder().decode(bytes));
    } catch (e) {
      throw new DraftCodecError({
        tag: "malformed",
        reason: `JSON parse failed: ${e instanceof Error ? e.message : String(e)}`,
      });
    }

    if (typeof raw !== "object" || raw === null) {
      throw new DraftCodecError({ tag: "malformed", reason: "not an object" });
    }

    const obj = raw as Record<string, unknown>;

    if (typeof obj["schema"] !== "number") {
      throw new DraftCodecError({
        tag: "malformed",
        reason: "missing or non-numeric schema",
      });
    }
    if (obj["schema"] !== DRAFT_SCHEMA_VERSION) {
      throw new DraftCodecError({
        tag: "unsupportedSchema",
        version: obj["schema"] as number,
      });
    }
    if (typeof obj["channelId"] !== "string" || obj["channelId"] === "") {
      throw new DraftCodecError({ tag: "malformed", reason: "empty channelId" });
    }
    if (typeof obj["text"] !== "string") {
      throw new DraftCodecError({
        tag: "malformed",
        reason: "missing or non-string text",
      });
    }
    if (typeof obj["ts"] !== "number") {
      throw new DraftCodecError({
        tag: "malformed",
        reason: "missing or non-numeric ts",
      });
    }

    const textLen = countCodePoints(obj["text"] as string);
    if (textLen > DraftCodec.MAX_DRAFT_LENGTH) {
      throw new DraftCodecError({ tag: "tooLarge", count: textLen });
    }

    return {
      schema: obj["schema"] as number,
      channelId: obj["channelId"] as string,
      text: obj["text"] as string,
      ts: obj["ts"] as number,
    };
  },

  /**
   * Convenience: returns `null` for non-drafts. Throws only for malformed
   * payloads that DID claim to be drafts (i.e. had the prefix but were
   * broken inside). Mirrors iOS `DraftCodec.tryDecode`.
   */
  tryDecode(plaintext: string): DraftPayload | null {
    if (!DraftCodec.isDraft(plaintext)) return null;
    return DraftCodec.decode(plaintext);
  },
} as const;

// ---------------------------------------------------------------------------
// Base64url helpers (no external dependency)
// ---------------------------------------------------------------------------

function base64UrlEncode(data: Uint8Array): string {
  // btoa works on binary strings
  let binary = "";
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]!);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlDecode(str: string): Uint8Array {
  // Restore standard base64 padding
  const padded =
    str.replace(/-/g, "+").replace(/_/g, "/") +
    "==".slice(0, (4 - (str.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
